import React, { useEffect, useState } from "react";
import { LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer } from "recharts";

// Format number utility
const formatNumber = (num) => {
  return parseFloat(num).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

// API Base URL - Update this to your actual backend URL
const BACKEND_LINK = "https://aethenos.com:2053/aethenos-api";
// const BACKEND_LINK = "https://api-aethenos.exonsoftware.lk";
// const BACKEND_LINK = "http://localhost:8082";

// Get current user token from cookies
const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
};

const CURRENT_USER = getCookie("aethenos");

// API function to get revenue overview
const GetRevenueOverview = (setter) => {
  const myHeaders = new Headers();
  myHeaders.append("Authorization", `Bearer ${CURRENT_USER}`);

  const requestOptions = {
    method: "GET",
    headers: myHeaders,
    redirect: "follow",
  };

  fetch(`${BACKEND_LINK}/revenue/getInstructorRevenueOverview`, requestOptions)
    .then((response) => response.json())
    .then((result) => {
      console.log("Revenue Overview:", result);
      setter(result);
    })
    .catch((error) => {
      console.error("Error fetching revenue overview:", error);
      setter(null);
    });
};

// API function to get chart data
const GetChartOverviewData = (setter) => {
  const myHeaders = new Headers();
  myHeaders.append("Authorization", `Bearer ${CURRENT_USER}`);

  const requestOptions = {
    method: "GET",
    headers: myHeaders,
    redirect: "follow",
  };

  fetch(
    `${BACKEND_LINK}/revenue/getInstructorChartDetailsForThisMonth`,
    requestOptions
  )
    .then((response) => response.json())
    .then((result) => {
      console.log("Chart Data:", result);
      setter(result);
    })
    .catch((error) => {
      console.error("Error fetching chart data:", error);
      setter(null);
    });
};

const CustomTooltip = ({ active, payload, label, formatter }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{
        background: "white",
        padding: "12px 16px",
        borderRadius: "8px",
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        border: '1px solid #e5e7eb'
      }}>
        <p style={{
          margin: 0,
          fontWeight: "600",
          fontSize: '12px',
          color: '#6b7280',
          marginBottom: '8px',
          textTransform: 'uppercase',
          letterSpacing: '0.05em'
        }}>
          Day {label}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            background: payload[0].color
          }} />
          <p style={{
            margin: 0,
            color: '#111827',
            fontSize: '15px',
            fontWeight: '700'
          }}>
            {formatter(payload[0].value)}
          </p>
        </div>
      </div>
    );
  }
  return null;
};

function RevenueReport() {
  const [overViewStatus, setOverViewStatus] = useState(null);
  const [chartData, setChartData] = useState(null);
  const [activeTab, setActiveTab] = useState("revenue");
  const [loading, setLoading] = useState(true);

  const currentMonth = new Date().toLocaleString('default', { month: 'long' });

  useEffect(() => {
    let mounted = true;

    // Fetch data independently to show as soon as available
    GetRevenueOverview((data) => {
      if (mounted) {
        setOverViewStatus(data);
        // If chart data is already loaded, stop loading
        if (chartData) {
          setLoading(false);
        }
      }
    });

    GetChartOverviewData((data) => {
      if (mounted) {
        setChartData(data);
        // If overview data is already loaded, stop loading
        if (overViewStatus) {
          setLoading(false);
        }
      }
    });

    return () => {
      mounted = false;
    };
  }, []);

  // Show loading only if neither data has loaded yet
  if (loading && !overViewStatus && !chartData) {
    return (
      <div className="all-courses-container mb-5">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '500px',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            border: '5px solid #f3f3f3',
            borderTop: '5px solid #e01d20',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <p style={{ color: '#6b7280', fontSize: '15px', fontWeight: '500' }}>Loading dashboard data...</p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      </div>
    );
  }

  // Show overview data even if chart is still loading
  if (!overViewStatus) {
    return (
      <div className="all-courses-container mb-5">
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '500px',
          flexDirection: 'column',
          gap: '20px'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            border: '5px solid #f3f3f3',
            borderTop: '5px solid #e01d20',
            borderRadius: '50%',
            animation: 'spin 0.8s linear infinite'
          }} />
          <p style={{ color: '#6b7280', fontSize: '15px', fontWeight: '500' }}>Loading overview data...</p>
        </div>
      </div>
    );
  }

  // Create data for chart (empty array if chart data not loaded yet)
  const data = chartData ? chartData.days.map((day, index) => ({
    day,
    revenue: chartData.revenue[index],
    enrollments: chartData.enrollment[index],
    rating: chartData.rating[index] || 0,
  })) : [];

  const tabs = [
    {
      key: "revenue",
      label: "TOTAL REVENUE",
      value: `£${formatNumber(parseFloat(overViewStatus.totalRevenue))}`,
      subtitle: `+£ ${formatNumber(parseFloat(overViewStatus.thisMonthRevenue))} this month`,
      color: "#4285F4",
      dataKey: "revenue",
      yLabel: "Revenue (GBP)",
      formatter: (val) => `Revenue: £${val.toFixed(2)}`,
      icon: "💰"
    },
    {
      key: "enrollment",
      label: "TOTAL ENROLLMENT",
      value: overViewStatus.totalEnrollments.toLocaleString(),
      subtitle: `+${overViewStatus.thisMonthEnrollments} this month`,
      color: "#34A853",
      dataKey: "enrollments",
      yLabel: "Enrollments",
      formatter: (val) => `Enrollments: ${val}`,
      icon: "👥"
    },
    {
      key: "rating",
      label: "INSTRUCTOR RATING",
      value: overViewStatus.instructorRating === "NaN" ? "0.00" : parseFloat(overViewStatus.instructorRating).toFixed(2),
      subtitle: `+${overViewStatus.thisMonthRating} rating this month`,
      color: "#FFA500",
      dataKey: "rating",
      yLabel: "Ratings (0-5)",
      formatter: (val) => `Rating: ${val.toFixed(1)}/5`,
      icon: "⭐"
    }
  ];

  const activeTabData = tabs.find(t => t.key === activeTab);

  return (
    <div className="all-courses-container mb-5">
      {/* Header */}
      <div className="row mb-4 mx-3" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h4 style={{ margin: 0, padding: 0, fontWeight: '700', fontSize: '26px', color: '#111827' }}>
            Dashboard
          </h4>
          <span style={{ padding: 0, margin: 0, fontSize: '14px', color: '#6b7280' }}>
            Get top insights about your performance
          </span>
        </div>
      </div>

      {/* Revenue Report Link */}
      <div className="text-right m-4">
        <a
          className="btn btn-danger"
          href="/performance/revenue-report"
          style={{
            textDecoration: 'none',
            boxShadow: '0 2px 4px rgba(224, 29, 32, 0.2)',
            transition: 'all 0.2s',
            fontWeight: '500'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'translateY(-1px)';
            e.currentTarget.style.boxShadow = '0 4px 8px rgba(224, 29, 32, 0.3)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 2px 4px rgba(224, 29, 32, 0.2)';
          }}
        >
          Revenue Report <i className="fa-solid fa-angle-right"></i>
        </a>
      </div>

      {/* Card Container */}
      <div className="card border-rad-20" style={{
        boxShadow: '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.08)',
        overflow: 'hidden',
        border: '1px solid #e5e7eb'
      }}>
        <div className="row m-0">
          {/* Tab Headers */}
          <div style={{
            display: 'flex',
            borderBottom: '2px solid #f3f4f6',
            overflowX: 'auto',
            flexWrap: 'wrap',
            width: '100%',
            background: '#fafafa'
          }}>
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                style={{
                  flex: '1 1 250px',
                  minWidth: '200px',
                  padding: '20px 24px',
                  border: 'none',
                  background: activeTab === tab.key ? '#ffffff' : 'transparent',
                  borderBottom: activeTab === tab.key ? `3px solid ${tab.color}` : '3px solid transparent',
                  cursor: 'pointer',
                  textAlign: 'left',
                  position: 'relative',
                  transition: 'all 0.3s ease',
                  transform: activeTab === tab.key ? 'translateY(-2px)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.background = '#ffffff';
                  }
                }}
                onMouseLeave={(e) => {
                  if (activeTab !== tab.key) {
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                {/* Active indicator dot */}
                {activeTab === tab.key && (
                  <div style={{
                    position: 'absolute',
                    top: '16px',
                    right: '16px',
                    width: '8px',
                    height: '8px',
                    borderRadius: '50%',
                    background: tab.color,
                    animation: 'pulse 2s infinite'
                  }} />
                )}

                <div style={{
                  fontSize: '11px',
                  fontWeight: '600',
                  color: '#9ca3af',
                  marginBottom: '10px',
                  letterSpacing: '0.8px'
                }}>
                  {tab.label}
                </div>
                <div style={{
                  fontSize: '32px',
                  fontWeight: '700',
                  color: '#111827',
                  marginBottom: '8px',
                  lineHeight: '1.1'
                }}>
                  {tab.value}
                </div>
                <div style={{
                  fontSize: '13px',
                  color: '#6b7280',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}>
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path d="M5 1L5 9M1 5L9 5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  {tab.subtitle}
                </div>
              </button>
            ))}
          </div>

          {/* Chart Area */}
          <div style={{ padding: '32px 28px', background: '#ffffff', width: '100%' }}>
            {/* Chart Header */}
            <div style={{ marginBottom: '24px', paddingBottom: '20px', borderBottom: '2px solid #f3f4f6' }}>
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '16px'
              }}>
                <div>
                  <h5 style={{
                    margin: 0,
                    fontSize: '18px',
                    fontWeight: '700',
                    color: '#111827',
                    marginBottom: '4px'
                  }}>
                    {activeTabData.label.charAt(0) + activeTabData.label.slice(1).toLowerCase()} Trend
                  </h5>
                  <p style={{ margin: 0, fontSize: '14px', color: '#6b7280' }}>
                    Daily performance for {currentMonth}
                  </p>
                </div>

                {/* Legend Badge */}
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  padding: '8px 16px',
                  background: '#f9fafb',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px'
                }}>
                  <div style={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background: activeTabData.color,
                    boxShadow: `0 0 0 3px ${activeTabData.color}20`
                  }} />
                  <span style={{ fontSize: '13px', fontWeight: '600', color: '#374151' }}>
                    {activeTabData.yLabel}
                  </span>
                </div>
              </div>
            </div>

            {/* Chart */}
            {!chartData ? (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '420px',
                flexDirection: 'column',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  border: '4px solid #f3f3f3',
                  borderTop: '4px solid #e01d20',
                  borderRadius: '50%',
                  animation: 'spin 0.8s linear infinite'
                }} />
                <p style={{ color: '#6b7280', fontSize: '13px', fontWeight: '500' }}>Loading chart data...</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={420}>
                <LineChart data={data} margin={{ top: 10, right: 10, left: 10, bottom: 10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />

                  <XAxis
                    dataKey="day"
                    label={{
                      value: `Days of ${currentMonth}`,
                      position: "insideBottom",
                      offset: -5,
                      style: { fontSize: '13px', fill: '#6b7280', fontWeight: '600' }
                    }}
                    interval="preserveStartEnd"
                    angle={-45}
                    textAnchor="end"
                    height={80}
                    tick={{ dy: 10, fontSize: 11, fill: '#9ca3af', fontWeight: '500' }}
                    stroke="#d1d5db"
                    strokeWidth={1.5}
                  />

                  <YAxis
                    label={{
                      value: activeTabData.yLabel,
                      angle: -90,
                      position: "insideLeft",
                      offset: 15,
                      style: { fontSize: '13px', fill: '#6b7280', fontWeight: '600' }
                    }}
                    tick={{ fontSize: 11, fill: '#9ca3af', fontWeight: '500' }}
                    stroke="#d1d5db"
                    strokeWidth={1.5}
                    domain={activeTab === "rating" ? [0, 5] : ['auto', 'auto']}
                  />

                  <Tooltip
                    content={<CustomTooltip formatter={activeTabData.formatter} />}
                    cursor={{ stroke: activeTabData.color, strokeWidth: 2, strokeDasharray: '5 5', opacity: 0.3 }}
                  />

                  <Line
                    type="monotone"
                    dataKey={activeTabData.dataKey}
                    stroke={activeTabData.color}
                    strokeWidth={3.5}
                    dot={{
                      r: 5,
                      fill: '#ffffff',
                      strokeWidth: 3,
                      stroke: activeTabData.color,
                    }}
                    activeDot={{
                      r: 8,
                      fill: activeTabData.color,
                      stroke: '#ffffff',
                      strokeWidth: 3,
                    }}
                  />
                </LineChart>
              </ResponsiveContainer>
            )}

            {/* Quick Stats Footer */}
            <div style={{ marginTop: '28px', paddingTop: '24px', borderTop: '2px solid #f3f4f6' }}>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
                gap: '16px'
              }}>
                {tabs.map((tab) => (
                  <div
                    key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    style={{
                      padding: '18px 20px',
                      background: activeTab === tab.key ? `${tab.color}08` : '#f9fafb',
                      border: `2px solid ${activeTab === tab.key ? tab.color + '40' : '#e5e7eb'}`,
                      borderRadius: '12px',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      position: 'relative',
                      overflow: 'hidden'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <div>
                        <p style={{
                          margin: 0,
                          fontSize: '11px',
                          color: '#6b7280',
                          fontWeight: '600',
                          textTransform: 'uppercase',
                          letterSpacing: '0.5px',
                          marginBottom: '6px'
                        }}>
                          {tab.label}
                        </p>
                        <p style={{
                          margin: 0,
                          fontSize: '22px',
                          fontWeight: '700',
                          color: tab.color
                        }}>
                          {tab.value}
                        </p>
                      </div>
                      <div style={{
                        width: '44px',
                        height: '44px',
                        borderRadius: '12px',
                        background: `${tab.color}15`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '22px'
                      }}>
                        {tab.icon}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
      `}</style>
    </div>
  );
}

export default RevenueReport;